import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Network, Users, DollarSign, CheckCircle, Clock,
  Star, RefreshCw, Search, ChevronLeft, Shield,
  Zap, TrendingUp, ArrowUpRight, AlertCircle, Filter
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ADMIN_EMAILS = [
  "jamesdehart@dealforge.pro",
  "jdehart06@gmail.com",
  "TheDeHartInvestmentGroup@outlook.com",
  "admin@dealforge.com"
];

const STATUS_CONFIG = {
  signed_up: {
    label: "Signed Up",
    color: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    next: "Mark 1st Payment",
    nextColor: "bg-[#D4AF37] hover:bg-[#C4A030] text-black"
  },
  paying: {
    label: "Active (1 payment)",
    color: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/20",
    next: "Confirm 2nd Payment → Release Credits",
    nextColor: "bg-emerald-600 hover:bg-emerald-500 text-white"
  },
  reward_unlocked: {
    label: "Reward Unlocked",
    color: "text-[#00FF88] bg-emerald-500/10 border-emerald-500/20",
    next: null
  },
  pending: {
    label: "Pending",
    color: "text-white/30 bg-white/[0.04] border-white/[0.08]",
    next: "Mark 1st Payment",
    nextColor: "bg-[#D4AF37] hover:bg-[#C4A030] text-black"
  }
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-[10px] font-bold border rounded-full uppercase tracking-wider ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function StatCard({ label, value, sub, color = "text-white", icon: Icon, accent }) {
  return (
    <div className={`bg-[#0A0A0A] border rounded-xl p-5 ${accent || "border-white/[0.06]"}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-widest font-semibold mb-2">{label}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-white/30 text-xs mt-1">{sub}</p>}
        </div>
        {Icon && (
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-white/[0.04]`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminReferralsPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const isAdmin = ADMIN_EMAILS.includes(user?.email);

  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [platformStats, setPlatformStats] = useState(null);

  const fetchReferrals = useCallback(async () => {
    try {
      const url = statusFilter === "all"
        ? `${BACKEND_URL}/api/referrals/admin/all`
        : `${BACKEND_URL}/api/referrals/admin/all?status=${statusFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      setReferrals(res.data.referrals || []);
      setPlatformStats(res.data.by_status || null);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { if (isAdmin) fetchReferrals(); }, [isAdmin, fetchReferrals]);

  const handleMarkPaying = async (referralId) => {
    setActionLoading(referralId);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/referrals/admin/mark-paying/${referralId}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      // Update local state optimistically
      setReferrals(prev => prev.map(r => {
        if (r.referral_id !== referralId) return r;
        if (!r.first_payment_date) return { ...r, status: "paying", first_payment_date: new Date().toISOString() };
        return { ...r, status: "reward_unlocked", second_payment_date: new Date().toISOString(), reward_unlocked_at: new Date().toISOString() };
      }));
      fetchReferrals(); // Refresh for accurate data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = referrals.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      r.referred_email?.toLowerCase().includes(s) ||
      r.referred_name?.toLowerCase().includes(s) ||
      r.referrer_email?.toLowerCase().includes(s) ||
      r.referral_code?.toLowerCase().includes(s)
    );
  });

  const totalCreditsIssued = referrals
    .filter(r => r.status === "reward_unlocked")
    .reduce((sum, r) => sum + (r.credit_amount || 25), 0);

  const revenueProtected = referrals
    .filter(r => r.status === "reward_unlocked")
    .reduce((sum, r) => sum + (99 - (r.credit_amount || 25)), 0);

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen bg-black">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={mainClass}>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Shield className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/30">Admin access required</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const tabs = [
    { key: "all",            label: "All",            count: referrals.length },
    { key: "signed_up",      label: "Signed Up",      count: platformStats?.signed_up || 0 },
    { key: "paying",         label: "Active",         count: platformStats?.paying || 0 },
    { key: "reward_unlocked",label: "Rewarded",       count: platformStats?.reward_unlocked || 0 },
  ];

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      <main className={mainClass}>
        <div className="p-6 max-w-7xl">

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-6">
            <Link to="/admin" className="flex items-center gap-1.5 text-white/40 hover:text-white text-sm transition-colors">
              <ChevronLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-white/20">/</span>
            <span className="text-white/60 text-sm">Network Credits</span>
          </div>

          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Network className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[10px] text-[#D4AF37] uppercase tracking-widest font-semibold">Admin</span>
              </div>
              <h1 className="text-3xl font-bold text-white">Network Credits Dashboard</h1>
              <p className="text-white/40 text-sm mt-1">Approve referrals, release credits, monitor the program's health</p>
            </div>
            <Button
              onClick={fetchReferrals}
              variant="outline"
              className="border-white/[0.08] text-white/50 hover:text-white gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <StatCard
              label="Total Referrals"
              value={referrals.length}
              sub="across all users"
              icon={Users}
            />
            <StatCard
              label="Pending Approval"
              value={platformStats?.signed_up || 0}
              sub="awaiting 1st payment"
              color="text-blue-400"
              icon={Clock}
            />
            <StatCard
              label="Credits Issued"
              value={`$${totalCreditsIssued.toFixed(0)}`}
              sub={`${platformStats?.reward_unlocked || 0} rewarded referrals`}
              color="text-[#D4AF37]"
              icon={Zap}
              accent="border-[#D4AF37]/20"
            />
            <StatCard
              label="Revenue Protected"
              value={`$${revenueProtected.toFixed(0)}`}
              sub="net subscription revenue"
              color="text-[#00FF88]"
              icon={TrendingUp}
            />
          </div>

          {/* Policy reminder */}
          <div className="flex items-start gap-3 p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl mb-6">
            <Shield className="w-4 h-4 text-white/30 flex-shrink-0 mt-0.5" />
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {[
                "$25 / referral ($50 lender · $75 title)",
                "Released after 2nd billing cycle",
                "$200 lifetime cap · 8 referrals max",
                "50% subscription cap = $49.50/mo",
                "Non-transferable · Platform use only"
              ].map(rule => (
                <span key={rule} className="text-white/30 text-xs">{rule}</span>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-1 p-1 bg-white/[0.03] border border-white/[0.06] rounded-lg">
              {tabs.map(({ key, label, count }) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-all ${
                    statusFilter === key
                      ? "bg-[#D4AF37] text-black"
                      : "text-white/40 hover:text-white"
                  }`}
                  data-testid={`filter-${key}`}
                >
                  {label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${statusFilter === key ? "bg-black/20 text-black" : "bg-white/10 text-white/50"}`}>
                    {count}
                  </span>
                </button>
              ))}
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by email, name, code…"
                className="pl-8 h-8 text-xs w-64 bg-white/[0.03] border-white/[0.08] text-white"
                data-testid="search-referrals"
              />
            </div>
          </div>

          {/* Referrals Table */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 px-5 py-3 border-b border-white/[0.06] text-[10px] text-white/20 uppercase tracking-wider font-semibold">
              <span className="col-span-3">Referred Operator</span>
              <span className="col-span-3">Referred By</span>
              <span className="col-span-2">Signed Up</span>
              <span className="col-span-1">Credit</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-1 text-right">Action</span>
            </div>

            {loading ? (
              <div className="py-16 text-center">
                <RefreshCw className="w-6 h-6 text-white/20 animate-spin mx-auto" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center">
                <Network className="w-10 h-10 text-white/[0.06] mx-auto mb-3" />
                <p className="text-white/30 text-sm">
                  {search ? "No referrals match your search" : "No referrals yet"}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {filtered.map((ref) => {
                  const cfg = STATUS_CONFIG[ref.status] || STATUS_CONFIG.pending;
                  const isActing = actionLoading === ref.referral_id;
                  const canAct = ref.status === "signed_up" || ref.status === "paying" || ref.status === "pending";

                  return (
                    <div
                      key={ref.referral_id}
                      className="grid grid-cols-12 items-center px-5 py-4 hover:bg-white/[0.01] transition-all"
                      data-testid={`referral-row-${ref.referral_id}`}
                    >
                      {/* Referred */}
                      <div className="col-span-3">
                        <p className="text-white text-sm font-medium truncate">{ref.referred_name || "—"}</p>
                        <p className="text-white/30 text-xs truncate">{ref.referred_email}</p>
                        {ref.referred_portal_type && (
                          <span className="text-[10px] text-white/20 capitalize">{ref.referred_portal_type}</span>
                        )}
                      </div>

                      {/* Referrer */}
                      <div className="col-span-3">
                        <p className="text-white/60 text-xs truncate">{ref.referrer_email || "—"}</p>
                        <p className="text-white/30 text-[10px] font-mono">{ref.referral_code}</p>
                        {ref.source === "contract_signature" && (
                          <span className="text-[10px] text-purple-400">via contract sign</span>
                        )}
                      </div>

                      {/* Dates */}
                      <div className="col-span-2">
                        <p className="text-white/50 text-xs">
                          {ref.signup_date ? new Date(ref.signup_date).toLocaleDateString() : "—"}
                        </p>
                        {ref.first_payment_date && (
                          <p className="text-[10px] text-[#D4AF37] mt-0.5">
                            1st: {new Date(ref.first_payment_date).toLocaleDateString()}
                          </p>
                        )}
                        {ref.second_payment_date && (
                          <p className="text-[10px] text-[#00FF88] mt-0.5">
                            2nd: {new Date(ref.second_payment_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>

                      {/* Credit amount */}
                      <div className="col-span-1">
                        <span className={`font-bold text-sm ${ref.status === "reward_unlocked" ? "text-[#00FF88]" : "text-white/30"}`}>
                          ${ref.credit_amount?.toFixed(0) || 25}
                        </span>
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <StatusBadge status={ref.status} />
                      </div>

                      {/* Action */}
                      <div className="col-span-1 flex justify-end">
                        {canAct ? (
                          <Button
                            onClick={() => handleMarkPaying(ref.referral_id)}
                            disabled={isActing}
                            size="sm"
                            className={`text-[10px] h-7 px-2.5 font-semibold ${cfg.nextColor}`}
                            data-testid={`mark-paying-${ref.referral_id}`}
                          >
                            {isActing
                              ? <RefreshCw className="w-3 h-3 animate-spin" />
                              : ref.status === "paying"
                                ? <><CheckCircle className="w-3 h-3 mr-1" />Confirm</>
                                : <><ArrowUpRight className="w-3 h-3 mr-1" />1st Pay</>
                            }
                          </Button>
                        ) : (
                          <div className="flex items-center gap-1 text-[#00FF88]">
                            <Star className="w-3.5 h-3.5" />
                            <span className="text-[10px] font-semibold">Done</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Two-step reminder */}
          <div className="mt-4 flex items-start gap-3 p-4 bg-[#D4AF37]/[0.04] border border-[#D4AF37]/20 rounded-xl">
            <AlertCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <p className="text-white/40 text-xs leading-relaxed">
              <span className="text-[#D4AF37] font-semibold">Two-step approval:</span> Click <em>"1st Pay"</em> when you confirm the referred user's first payment. Click <em>"Confirm"</em> again after their second billing cycle — this releases the $25 credit to the referrer's account instantly. Credits are platform-only and capped at $200 lifetime.
            </p>
          </div>

        </div>
      </main>
    </div>
  );
}
